import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root';
import { useMaybeAppData } from '~/modules/root/hook/use-app-data';

/** Thousands separators, fixed to one locale so the server and browser agree. */
const formatCount = (count: number) =>
    new Intl.NumberFormat('en-NZ').format(count);

interface FooterAudienceCounterProps {
    /** Headline and figure colour. */
    textClassName: string;
    /** Description and label colour. */
    mutedClassName: string;
    className?: string;
}

/**
 * The footer's audience counter (Figma "Footer counter", Frame 1): unique
 * visitors across all markets since 1 January, from the daily Snowflake sync,
 * so every market's site shows the same figure. Left out without that figure.
 * Drawn only with `revamp_2026` on.
 *
 * Sizes are the Figma px over the 1728px frame's root font size (14.394px), so
 * the counter scales with the rem like the rest of the footer and the
 * description keeps the design's two-line wrap.
 */
export const FooterAudienceCounter = (props: FooterAudienceCounterProps) =>
    useFeature('revamp_2026') ? <AudienceCounter {...props} /> : null;

const AudienceCounter = ({
    textClassName,
    mutedClassName,
    className,
}: FooterAudienceCounterProps) => {
    const monthlyUsers = useMaybeAppData()?.monthlyUsers;
    if (!monthlyUsers?.yearToDateUsers) return null;

    return (
        <div className={cn('flex flex-col font-sans', className)}>
            <p
                className={cn(
                    'm-0 text-[3.1834rem] leading-[3.8904rem] font-normal tabular-nums',
                    textClassName,
                )}
            >
                {formatCount(monthlyUsers.yearToDateUsers)}
            </p>
            <p
                className={cn(
                    'm-0 mt-[0.7861rem] text-[1.0314rem] leading-[1.2505rem] font-medium',
                    textClassName,
                )}
            >
                People used ArchiPro since January {monthlyUsers.yearToDateYear}
            </p>
            <p
                className={cn(
                    'm-0 mt-[0.1389rem] max-w-[23.642rem] text-[1.0314rem] leading-[1.2505rem] font-normal',
                    mutedClassName,
                )}
            >
                Connecting with trusted professionals, products, projects and
                publications for their next space.
            </p>
        </div>
    );
};
